// src/features/scheduleBuilder/slices/publishHistoryEvents/slice.ts
import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { fetchJSON } from 'api/fetch';
import { RootState } from 'initializers/types';

import { PARENT_SLICE_NAME, SLICE_NAME } from './constants';
import {
  PublishHistoryEvent,
  PublishHistoryEventPayloadItem,
  PublishHistoryEventsParams,
  PublishHistoryEventsPayload,
  PublishHistoryEventsState,
} from './types';

export const fetchPublishHistoryEvents = createAsyncThunk<
  PublishHistoryEventsPayload,
  PublishHistoryEventsParams
>(
  `${SLICE_NAME}/fetch`,
  async ({ limit = 10 }: PublishHistoryEventsParams = {}) => {
    const resp = await fetchJSON(
      `/schedule_builder/publish_history_events?limit=${limit}`
    );
    return resp;
  }
);

const publishHistoryEventsAdapter = createEntityAdapter<PublishHistoryEvent>({
  sortComparer: (a, b) =>
    new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
});

export const initialState: PublishHistoryEventsState =
  publishHistoryEventsAdapter.getInitialState({
    status: 'idle',
    error: undefined,
  });

const getScheduleBuilderState = (state: RootState) =>
  state.get(PARENT_SLICE_NAME);

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    clearPublishHistoryEvents: publishHistoryEventsAdapter.removeAll,
  },
  extraReducers: builder => {
    builder.addCase(fetchPublishHistoryEvents.pending, state => {
      state.status = 'pending';
    });
    builder.addCase(fetchPublishHistoryEvents.fulfilled, (state, action) => {
      const items = action.payload.data.map(
        (item: PublishHistoryEventPayloadItem) => ({
          id: item.id,
          type: item.type,
          ...item.attributes,
        })
      );
      publishHistoryEventsAdapter.setAll(state, items);
      state.status = 'succeeded';
    });
    builder.addCase(fetchPublishHistoryEvents.rejected, (state, action) => {
      state.status = 'failed';
      state.error =
        action.error.message || 'Failed to fetch publish history events';
    });
  },
});

export const reducer = slice.reducer;
export const { clearPublishHistoryEvents } = slice.actions;

export const {
  selectById: selectPublishHistoryEventById,
  selectIds: selectPublishHistoryEventIds,
  selectEntities: selectPublishHistoryEventEntities,
  selectAll: selectAllPublishHistoryEvents,
  selectTotal: selectTotalPublishHistoryEvents,
} = publishHistoryEventsAdapter.getSelectors<RootState>(
  state => getScheduleBuilderState(state)?.[SLICE_NAME] || initialState
);

export const selectPublishHistoryFetchIsLoading = (state: RootState): boolean =>
  getScheduleBuilderState(state)?.[SLICE_NAME]?.status === 'pending';
